import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { Info} from "@phosphor-icons/react";
import { certificationSchema, defaultCertification } from "@reactive-resume/schema";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RichInput,
  Tooltip,
} from "@reactive-resume/ui";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { AiActions } from "@/client/components/ai-actions";
import { useResumeStore } from "@/client/stores/resume";

import { SectionDialog } from "../sections/shared/section-dialog";
import { URLInput } from "../sections/shared/url-input";
import { MonthYearDatePicker } from "../sections/shared/month-year-date-picker";
import { MastersUnionListEditor } from "../sections/masters-union-summary";

const formSchema = certificationSchema;

type FormValues = z.infer<typeof formSchema>;

export const CertificationsDialog = ({ isLpuDomain}: { isLpuDomain: boolean }) => {
  const form = useForm<FormValues>({
    defaultValues: defaultCertification,
    resolver: zodResolver(formSchema),
  });
  
  const template = useResumeStore((state) => state.resume.data?.metadata?.template);
  const isMastersUnion = template === "mastersunion";

  return (
    <SectionDialog<FormValues> id="certifications" form={form} defaultValues={defaultCertification}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        
          <>
            <FormField
              name="name"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  {/* <FormLabel>{t({ message: "Certification", context: "Name of the Certification" })}</FormLabel> */}
                  <span className="font-medium text-sm">Certification</span>
                  <FormControl>
                    <Input {...field} placeholder="Certification Name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* <FormField
              name="issuer"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Issuer`}</FormLabel>
                  <span className="font-medium text-sm">Issuer</span>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            <FormField
              name="url"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  {/* <FormLabel>{t`Website`}</FormLabel> */}
                  <span className="font-medium text-sm">Issuer</span>
                  <FormControl>
                    <URLInput
                      {...field}
                      value={{
                        label: form.watch("issuer") ?? "",
                        href:
                          typeof field.value === "string"
                            ? field.value
                            : (field.value?.href ?? ""),
                      }}
                      placeholder="Issuer"
                      labelPlaceholder="https://udemy.com/certificate/UC-..."
                      disableAutoHttps={true}
                      swapLabelAndHref={true}
                      onChange={(value) => {
                        form.setValue("issuer", value.label, { shouldValidate: true });
                        field.onChange({
                          ...(typeof field.value === "object" ? field.value : {}),
                          href: value.href,
                        });
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="date"
              control={form.control}
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <span className="font-medium text-sm">Issue Date</span>
                  <FormControl>
                    <MonthYearDatePicker value={field.value} onChange={field.onChange} showEnd={!isMastersUnion} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* <FormField
              name="location"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Location`}</FormLabel>
                  <span className="font-medium text-sm">Location</span>
                  <FormControl>
                    <Input {...field} placeholder="City, Country" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}
          </>
        

        <FormField
          name="summary"
          control={form.control}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              {/* <FormLabel>{t`Summary`}</FormLabel> */}
              <FormLabel className="flex items-center gap-2">
                <span>{t`Summary`}</span>
                {isMastersUnion && (
                  <Tooltip content="Click on “Add More” to add more bullet points">
                    <span className="inline-flex cursor-help text-muted-foreground">
                      <Info size={14} />
                    </span>
                  </Tooltip>
                )}
              </FormLabel>
              <FormControl>
                {isMastersUnion ? (
                  <MastersUnionListEditor
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                ) : (
                  <RichInput
                    {...field}
                    content={field.value}
                    onChange={(value) => field.onChange(value)}
                    footer={(editor) => (
                      <AiActions
                        value={editor.getText()}
                        content={editor.getHTML()}
                        onChange={(value) => {
                          editor.commands.setContent(value);
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SectionDialog>
  );
};
